import React, { useState, useEffect } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Cookies from "js-cookie";
import config from "../../config";
import styles from "../css/css_pages/ClientManage.module.css";
import modalStyles from "../css/css_pages/schedulesPopUp.module.css";
import {
  FiSearch,
  FiRefreshCw,
  FiX,
  FiCalendar,
  FiUser,
  FiMapPin,
  FiFileText,
} from "react-icons/fi";
import { FaStar } from "react-icons/fa";

const Schedules = () => {
  const [schedules, setSchedules] = useState([]);
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);
  const [selectedModeFilter, setSelectedModeFilter] = useState(null);
  const [selectedTsoFilter, setSelectedTsoFilter] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [tsos, setTsos] = useState([]);
  const [activeView, setActiveView] = useState("current"); // "current" or "special"

  // Modals
  const [showViewModal, setShowViewModal] = useState(false);
  const [isLoadingScheduleDetails, setIsLoadingScheduleDetails] =
    useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [pdfSchedule, setPdfSchedule] = useState(null);

  // Schedule data
  const [currentSchedule, setCurrentSchedule] = useState(null);

  // Status and mode options
  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "in_progress", label: "In Progress" },
    { value: "confirmed", label: "Confirmed" },
    { value: "completed", label: "Completed" },
    { value: "closed", label: "Closed" },
    { value: "proposed", label: "Proposed" },
    { value: "rejected", label: "Rejected" },
  ];

  const modeOptions = [
    { value: "all", label: "All Modes" },
    { value: "onsite", label: "Onsite" },
    { value: "remote", label: "Remote" },
    { value: "special_onsite", label: "Special Onsite" },
    { value: "special_remote", label: "Special Remote" },
  ];

  // Fetch schedules and TSOs on component mount
  useEffect(() => {
    fetchData();
    fetchTsoList();
  }, []);

  // Apply client-side filtering when schedules or filters change
  useEffect(() => {
    filterSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    schedules,
    selectedStatusFilter,
    selectedModeFilter,
    selectedTsoFilter,
    startDate,
    endDate,
    searchTerm,
    activeView,
  ]);

  const filterSchedules = () => {
    let filtered = [...schedules];

    // Filter by view type: Current Schedules vs Special Visits
    if (activeView === "current") {
      // Current Schedules: exclude "proposed" status (proposed are special visits)
      filtered = filtered.filter(
        (schedule) => schedule.schedule_status !== "proposed",
      );
    } else if (activeView === "special") {
      // Special Visits: only show "proposed" status
      filtered = filtered.filter(
        (schedule) => schedule.schedule_status === "proposed",
      );
    }

    // Apply other filters on the view-filtered results
    if (selectedStatusFilter && selectedStatusFilter.value !== "all") {
      filtered = filtered.filter(
        (schedule) => schedule.schedule_status === selectedStatusFilter.value,
      );
    }

    if (selectedModeFilter && selectedModeFilter.value !== "all") {
      filtered = filtered.filter(
        (schedule) => schedule.schedule_mode === selectedModeFilter.value,
      );
    }

    if (selectedTsoFilter && selectedTsoFilter.value !== "all") {
      filtered = filtered.filter(
        (schedule) => schedule.tso_id === selectedTsoFilter.value,
      );
    }

    if (startDate) {
      filtered = filtered.filter((schedule) => {
        const scheduleDate = new Date(schedule.scheduled_date);
        return scheduleDate >= startDate;
      });
    }

    if (endDate) {
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      filtered = filtered.filter((schedule) => {
        const scheduleDate = new Date(schedule.scheduled_date);
        return scheduleDate <= endOfDay;
      });
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((schedule) => {
        const scheduleNo = (schedule.schedule_no || "")
          .toString()
          .toLowerCase();
        const scheduleDescription = (schedule.schedule_description || "")
          .toString()
          .toLowerCase();
        const customerName = (schedule.customer_name || "")
          .toString()
          .toLowerCase();
        const clientId = (schedule.client_id || "").toString().toLowerCase();
        const assignedTsoName = (schedule.tso_name || "")
          .toString()
          .toLowerCase();

        return (
          scheduleNo.includes(term) ||
          scheduleDescription.includes(term) ||
          customerName.includes(term) ||
          clientId.includes(term) ||
          assignedTsoName.includes(term)
        );
      });
    }

    // Sort by schedule_no in ascending order
    filtered.sort((a, b) => {
      const idA = parseInt(a.schedule_no || a.schedule_id || 0);
      const idB = parseInt(b.schedule_no || b.schedule_id || 0);
      return idA - idB;
    });

    setFilteredSchedules(filtered);
  };

  const fetchTsoList = async () => {
    try {
      const token = Cookies.get("authToken");
      if (!token) return;

      const response = await fetch(`${config.API_URL}/management/tso-list`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTsos(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Error fetching TSO list:", err);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefreshing(true);
    setError(null);

    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/get-schedules/1?page_size=10000`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) throw new Error("Failed to fetch schedules");

      const data = await response.json();
      console.log("Schedules data", data);

      if (data.success && data.response) {
        const { schedule: schedulesList } = data.response;

        setSchedules(Array.isArray(schedulesList) ? schedulesList : []);
        setFilteredSchedules(Array.isArray(schedulesList) ? schedulesList : []);
      } else {
        setSchedules([]);
        setFilteredSchedules([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const fetchScheduleById = async (scheduleId) => {
    setIsLoadingScheduleDetails(true);
    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/get-schedule-by-id/${scheduleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) throw new Error("Failed to fetch schedule details");

      const data = await response.json();
      console.log("Schedule details:", data);

      if (data.success && data.response) {
        setCurrentSchedule(data.response);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Error fetching schedule details:", error);
      setError(error.message);
      // Don't close modal on error, just show error state
    } finally {
      setIsLoadingScheduleDetails(false);
    }
  };

  const openViewModal = async (schedule) => {
    setCurrentSchedule(null); // Clear previous data
    setShowViewModal(true);
    await fetchScheduleById(schedule.schedule_id);
  };

  const closeModal = () => {
    setShowViewModal(false);
    setCurrentSchedule(null);
    setIsLoadingScheduleDetails(false);
  };

  const handleFilterChange = (selected, filterType) => {
    if (filterType === "status") {
      setSelectedStatusFilter(selected);
    } else if (filterType === "mode") {
      setSelectedModeFilter(selected);
    } else if (filterType === "tso") {
      setSelectedTsoFilter(selected);
    }
  };

  const clearFilters = () => {
    setSelectedStatusFilter(null);
    setSelectedModeFilter(null);
    setSelectedTsoFilter(null);
    setStartDate(null);
    setEndDate(null);
    setSearchTerm("");
  };

  const handleViewPDF = (schedule, event) => {
    event.stopPropagation(); // Prevent row click event
    setPdfSchedule(schedule);
    setShowLanguageModal(true);
  };

  const viewPDF = async (schedule_id, schedule_no, language) => {
    if (!schedule_id) {
      alert("Schedule ID not found");
      return;
    }

    const pdfWindow = window.open("", "_blank");

    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      const response = await fetch(`${config.API_URL}/client/generate-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          schedule_id: schedule_id,
          language: language,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to view PDF: ${response.statusText}`);
      }

      const pdfBlob = await response.blob();
      const pdfUrl = URL.createObjectURL(pdfBlob);

      if (pdfWindow) {
        pdfWindow.location.href = pdfUrl;
      } else {
        alert("Popup blocked. Please allow popups for this site.");
      }
    } catch (error) {
      console.error("Error viewing pdf:", error);
      alert("Failed to view PDF. Please try again.");
      if (pdfWindow) {
        pdfWindow.close();
      }
    }
  };

  const handleLanguageSelect = (event) => {
    const selectedLang = event.target.value;

    if (selectedLang) {
      setShowLanguageModal(false);
      viewPDF(pdfSchedule.schedule_id, pdfSchedule.schedule_no, selectedLang);
    }
  };

  const getModeClass = (mode) => {
    switch (mode?.toLowerCase()) {
      case "onsite":
        return styles.modeOnsite;
      case "remote":
        return styles.modeRemote;
      case "special_onsite":
        return styles.modeOnsite;
      case "special_remote":
        return styles.modeRemote;
      default:
        return "";
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return styles.statusPending;
      case "in_progress":
        return styles.statusInProgress;
      case "confirmed":
        return styles.statusConfirmed;
      case "completed":
        return styles.statusCompleted;
      case "closed":
        return styles.statusClosed;
      case "proposed":
        return styles.statusProposed;
      case "rejected":
        return styles.statusRejected;
      default:
        return "";
    }
  };

  const getModeLabel = (mode) => {
    switch (mode?.toLowerCase()) {
      case "onsite":
        return "Onsite";
      case "remote":
        return "Remote";
      case "special_onsite":
        return "Special Onsite";
      case "special_remote":
        return "Special Remote";
      default:
        return mode || "N/A";
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "Pending";
      case "in_progress":
        return "In Progress";
      case "confirmed":
        return "Confirmed";
      case "completed":
        return "Completed";
      case "closed":
        return "Closed";
      case "proposed":
        return "Proposed";
      case "rejected":
        return "Rejected";
      default:
        return status || "N/A";
    }
  };

  const formatDateTime = (dateString, showOnlyTime = false) => {
    if (!dateString || isNaN(Date.parse(dateString))) {
      return "Invalid Date";
    }

    const date = new Date(dateString);

    // Format as UTC time
    const options = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC", // Use UTC instead of local time
    };

    if (!showOnlyTime) {
      options.year = "numeric";
      options.month = "2-digit";
      options.day = "2-digit";
    }

    return date.toLocaleString("en-US", options).replace(",", "");
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === "0") return "N/A";
    return `$${parseFloat(amount).toLocaleString()}`;
  };

  const renderRatingStars = (rating) => {
    if (!rating || rating === 0) {
      return <span style={{ color: "#999" }}>-</span>;
    }

    return (
      <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
        {[...Array(5)].map((_, index) => (
          <FaStar
            key={index}
            size={14}
            style={{
              color: index < rating ? "#fbbf24" : "#e5e7eb",
            }}
          />
        ))}
        <span
          style={{
            marginLeft: "0.5rem",
            fontSize: "0.75rem",
            color: "#6b7280",
          }}
        >
          {rating}/5
        </span>
      </div>
    );
  };

  // Loading state
  if (isLoading && !isRefreshing) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <p>Loading schedules...</p>
      </div>
    );
  }

  // Error state
  if (error && !showViewModal) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorText}>Error: {error}</p>
        <button className={styles.retryButton} onClick={fetchData}>
          Retry
        </button>
      </div>
    );
  }

  // Main render
  return (
    <div className={styles.invoiceManagement}>
      <header className={styles.managementHeader}>
        <div className={styles.headerTitleRow}>
          <h1>Schedule Management</h1>
          <button
            className={styles.refreshButton}
            disabled={isRefreshing}
            onClick={fetchData}
          >
            <FiRefreshCw className={isRefreshing ? styles.refreshing : ""} />
          </button>
        </div>

        <div className={styles.filterSectionHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
            <h3 className={styles.filterSectionTitle}>Quick Filters</h3>
          </div>
          <div className={styles.quickFilterButtons}>
            <button
              className={styles.quickFilterBtn}
              onClick={() => {
                const today = new Date();
                setStartDate(today);
                setEndDate(today);
              }}
            >
              Today
            </button>
            <button
              className={styles.quickFilterBtn}
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
              }}
            >
              All
            </button>
            <button
              className={`${styles.quickFilterBtn} ${styles.clearBtn}`}
              onClick={clearFilters}
            >
              Clear All
            </button>
          </div>
        </div>

        <div className={styles.headerControls}>
          <div className={styles.filterGrid}>
            <Select
              options={statusOptions}
              value={selectedStatusFilter}
              onChange={(selected) => handleFilterChange(selected, "status")}
              styles={customSelectStyles}
              placeholder="Status"
              isClearable
              className={styles.filterSelect}
            />
            <Select
              options={modeOptions}
              value={selectedModeFilter}
              onChange={(selected) => handleFilterChange(selected, "mode")}
              styles={customSelectStyles}
              placeholder="Mode"
              isClearable
              className={styles.filterSelect}
            />
            <Select
              options={[
                { value: "all", label: "All TSOs" },
                ...tsos.map((tso) => ({
                  value: tso.tso_id,
                  label: tso.tso_calling_name || tso.tso_name,
                })),
              ]}
              value={selectedTsoFilter}
              onChange={(selected) => handleFilterChange(selected, "tso")}
              styles={customSelectStyles}
              placeholder="TSO"
              isClearable
              className={styles.filterSelect}
            />
            <div className={styles.datePickerWrapper}>
              <FiCalendar className={styles.calendarIcon} />
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                placeholderText="Start"
                dateFormat="MMM dd"
                className={styles.datePicker}
                isClearable
              />
            </div>
            <div className={styles.datePickerWrapper}>
              <FiCalendar className={styles.calendarIcon} />
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                placeholderText="End"
                dateFormat="MMM dd"
                className={styles.datePicker}
                isClearable
              />
            </div>
            <div className={styles.searchBar}>
              <FiSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </header>

      <div className={styles.invoicesContainer}>
        <div className={styles.tableResponsiveWrapper}>
          {filteredSchedules.length > 0 ? (
            <>
              <div className={styles.tableInfo}>
                <div>Showing {filteredSchedules.length} schedule(s)</div>
              </div>
              <div className={styles.tableScrollWrapper}>
                <table className={styles.invoicesTable}>
                  <thead>
                    <tr>
                      <th style={{ width: "60px" }}>#ID</th>
                      <th>Description</th>
                      <th style={{ width: "50px" }}>Mode</th>
                      <th>Client</th>
                      <th style={{ width: "50px" }}>Client ID</th>
                      <th style={{ width: "100px" }}>TSO</th>
                      <th style={{ width: "30px" }}>Checklist</th>
                      <th>Notes</th>
                      <th style={{ width: "120px" }}>Rating</th>
                      <th style={{ width: "100px" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSchedules.map((schedule, index) => {
                      // Check if any filter is active
                      const isFiltered =
                        selectedStatusFilter ||
                        selectedModeFilter ||
                        selectedTsoFilter ||
                        startDate ||
                        endDate ||
                        searchTerm;
                      // Show schedule ID when filters are active, otherwise show sequential numbers
                      const displayId = isFiltered
                        ? schedule.schedule_no || schedule.schedule_id || "-"
                        : index + 1;

                      return (
                        <tr
                          key={`${schedule.schedule_no}-${index}`}
                          className={styles.clickableRow}
                          onClick={() => openViewModal(schedule)}
                          style={{ cursor: "pointer" }}
                        >
                          <td>{displayId}</td>
                          <td>{schedule.schedule_description || "-"}</td>
                          <td>
                            <span
                              className={`${styles.modeBadge} ${getModeClass(
                                schedule.schedule_mode,
                              )}`}
                            >
                              {getModeLabel(schedule.schedule_mode)}
                            </span>
                          </td>
                          <td>{schedule.customer_name || "-"}</td>
                          <td>{schedule.client_id || "-"}</td>
                          <td>{schedule.tso_name || "Unassigned"}</td>
                          <td style={{ textAlign: "center" }}>
                            {schedule.checklist_report ? (
                              <button
                                onClick={(e) => handleViewPDF(schedule, e)}
                                className={styles.pdfButton}
                                title="View Checklist PDF"
                              >
                                <FiFileText size={20} />
                              </button>
                            ) : (
                              <span style={{ color: "#999" }}>-</span>
                            )}
                          </td>
                          <td className={styles.notesCell}>
                            <div
                              style={{ fontSize: "0.75rem", color: "#334155" }}
                            >
                              {schedule.officer_note && (
                                <div style={{ marginBottom: "0.5rem" }}>
                                  <strong>TSO:</strong> {schedule.officer_note}
                                </div>
                              )}
                              {schedule.client_note && (
                                <div>
                                  <strong>Client:</strong>{" "}
                                  {schedule.client_note}
                                </div>
                              )}
                              {!schedule.officer_note &&
                                !schedule.client_note && (
                                  <span style={{ color: "#999" }}>-</span>
                                )}
                            </div>
                          </td>
                          <td>{renderRatingStars(schedule.client_rating)}</td>
                          <td>
                            <span
                              className={`${
                                styles.statusBadge
                              } ${getStatusClass(schedule.schedule_status)}`}
                              style={{ whiteSpace: "nowrap" }}
                            >
                              {getStatusLabel(schedule.schedule_status)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className={styles.noResults}>
              <p>No schedules found matching your criteria.</p>
              {(selectedStatusFilter ||
                selectedModeFilter ||
                selectedTsoFilter ||
                startDate ||
                endDate ||
                searchTerm) && (
                <button className={styles.clearFilters} onClick={clearFilters}>
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Enhanced View Schedule Modal */}
      {showViewModal && (
        <div className={modalStyles.overlay} onClick={closeModal}>
          <div
            className={modalStyles.content}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={modalStyles.header}>
              <h2>
                Schedule Details
                {currentSchedule && ` (#${currentSchedule.schedule_no})`}
              </h2>
              <button className={modalStyles.closeBtn} onClick={closeModal}>
                <FiX />
              </button>
            </div>

            <div className={modalStyles.body}>
              {isLoadingScheduleDetails ? (
                <div className={modalStyles.loadingContainer}>
                  <div className={modalStyles.loadingSpinner} />
                  <p>Loading schedule details...</p>
                </div>
              ) : currentSchedule ? (
                <div className={modalStyles.detailsContainer}>
                  {/* Basic Information Section */}
                  <div className={modalStyles.section}>
                    <h3 className={modalStyles.sectionTitle}>
                      <FiUser /> Basic Information
                    </h3>
                    <div className={modalStyles.grid}>
                      <div className={modalStyles.item}>
                        <span className={modalStyles.label}>
                          Schedule Number:
                        </span>
                        <span className={modalStyles.value}>
                          {currentSchedule.schedule_no || "N/A"}
                        </span>
                      </div>
                      <div className={modalStyles.item}>
                        <span className={modalStyles.label}>Description:</span>
                        <span className={modalStyles.value}>
                          {currentSchedule.schedule_description || "N/A"}
                        </span>
                      </div>
                      <div className={modalStyles.item}>
                        <span className={modalStyles.label}>Type:</span>
                        <span className={modalStyles.value}>
                          {currentSchedule.schedule_type || "N/A"}
                        </span>
                      </div>
                      <div className={modalStyles.item}>
                        <span className={modalStyles.label}>Mode:</span>
                        <span
                          className={`${modalStyles.value} ${
                            modalStyles.modeBadge
                          } ${getModeClass(currentSchedule.schedule_mode)}`}
                        >
                          {getModeLabel(currentSchedule.schedule_mode)}
                        </span>
                      </div>
                      <div className={modalStyles.item}>
                        <span className={modalStyles.label}>Status:</span>
                        <span
                          className={`${modalStyles.value} ${
                            modalStyles.statusBadge
                          } ${getStatusClass(currentSchedule.schedule_status)}`}
                        >
                          {getStatusLabel(currentSchedule.schedule_status)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Schedule Information Section */}
                  <div className={modalStyles.section}>
                    <h3 className={modalStyles.sectionTitle}>
                      <FiCalendar /> Schedule Information
                    </h3>
                    <div className={modalStyles.grid}>
                      <div className={modalStyles.item}>
                        <span className={modalStyles.label}>
                          Scheduled Date:
                        </span>
                        <span className={modalStyles.value}>
                          {formatDateTime(currentSchedule.schedule_date)}
                        </span>
                      </div>
                      <div className={modalStyles.item}>
                        <span className={modalStyles.label}>Customer:</span>
                        <span className={modalStyles.value}>
                          {currentSchedule.customer_id || "N/A"}
                        </span>
                      </div>
                      <div className={modalStyles.item}>
                        <span className={modalStyles.label}>Assigned TSO:</span>
                        <span className={modalStyles.value}>
                          {currentSchedule.tso_id || "Unassigned"}
                        </span>
                      </div>
                      <div className={modalStyles.item}>
                        <span className={modalStyles.label}>
                          TSO Online ID:
                        </span>
                        <span className={modalStyles.value}>
                          {currentSchedule.tso_online_id || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* TSO Expenses Section */}
                  {currentSchedule.tso_expenses_id && (
                    <div className={modalStyles.section}>
                      <h3 className={modalStyles.sectionTitle}>
                        <FiMapPin /> TSO Expenses
                      </h3>
                      <div className={modalStyles.grid}>
                        <div className={modalStyles.item}>
                          <span className={modalStyles.label}>
                            Travelling Expenses:
                          </span>
                          <span className={modalStyles.value}>
                            {formatCurrency(
                              currentSchedule.tso_expenses_id
                                .travelling_expenses,
                            )}
                          </span>
                        </div>
                        <div className={modalStyles.item}>
                          <span className={modalStyles.label}>
                            Distance Travelled:
                          </span>
                          <span className={modalStyles.value}>
                            {currentSchedule.tso_expenses_id.distance_travelled
                              ? `${currentSchedule.tso_expenses_id.distance_travelled} km`
                              : "N/A"}
                          </span>
                        </div>
                        <div className={modalStyles.item}>
                          <span className={modalStyles.label}>
                            Transport Mode:
                          </span>
                          <span className={modalStyles.value}>
                            {currentSchedule.tso_expenses_id.transport_mode ||
                              "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Additional Information Section */}
                  {currentSchedule.description && (
                    <div className={modalStyles.section}>
                      <h3 className={modalStyles.sectionTitle}>
                        Additional Information
                      </h3>
                      <div className={modalStyles.item}>
                        <span className={modalStyles.label}>Description:</span>
                        <span className={modalStyles.value}>
                          {currentSchedule.description}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className={modalStyles.errorContainer}>
                  <p className={modalStyles.errorText}>
                    Failed to load schedule details. Please try again.
                  </p>
                  <button
                    className={modalStyles.retryBtn}
                    onClick={() =>
                      currentSchedule &&
                      fetchScheduleById(currentSchedule.schedule_id)
                    }
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Language Selection Modal */}
      {showLanguageModal && pdfSchedule && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "8px",
              maxWidth: "400px",
              width: "90%",
              position: "relative",
            }}
          >
            <h2 style={{ marginBottom: "20px", textAlign: "center" }}>
              Select Language
            </h2>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "20px",
              }}
            >
              <select
                onChange={handleLanguageSelect}
                defaultValue=""
                style={{
                  padding: "10px 20px",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "1rem",
                  minWidth: "200px",
                }}
              >
                <option value="" disabled>
                  Select a language...
                </option>
                <option value="si">Sinhala</option>
                <option value="ta">Tamil</option>
              </select>
            </div>
            <span
              className="close"
              onClick={() => setShowLanguageModal(false)}
              style={{
                position: "absolute",
                top: "10px",
                right: "15px",
                cursor: "pointer",
                fontSize: "24px",
                fontWeight: "bold",
                color: "#666",
              }}
            >
              &times;
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// Custom styles for react-select component
const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: "42px",
    padding: "0 12px",
    border: "1px solid #d1d5db",
    borderRadius: "10px",
    boxShadow: "none",
    backgroundColor: "white",
    transition: "all 0.2s ease",
    fontSize: "0.875rem",
    color: "#6b7280",
    "&:hover": {
      borderColor: "#9ca3af",
    },
    "&:focus": {
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
  }),
  input: (provided) => ({
    ...provided,
    fontSize: "0.875rem",
    color: "#6b7280",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#3b82f6"
      : state.isFocused
        ? "#f3f4f6"
        : "white",
    color: state.isSelected ? "white" : "#374151",
    fontSize: "0.875rem",
    padding: "0.75rem 1rem",
    "&:hover": {
      backgroundColor: state.isSelected ? "#3b82f6" : "#f3f4f6",
      color: state.isSelected ? "white" : "#111827",
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "10px",
    border: "1px solid #d1d5db",
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
    zIndex: 1000,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: "0.5rem 0",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#9ca3af",
    padding: "0 8px",
    "&:hover": {
      color: "#6b7280",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "0.875rem",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#6b7280",
    fontSize: "0.875rem",
  }),
};

export default Schedules;
